import { requestApi } from "./api";

export interface PageData<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
}

export interface AdminCredentials {
  id: string;
  password: string;
}

export interface MemberSummary {
  memberCode: string;
  loginId: string | null;
  name: string;
  phone: string | null;
  email: string;
  joinedAt: string;
  status: "ACTIVE" | "SUSPENDED" | "WITHDRAWN";
}

export interface MemberMemo {
  id: number;
  content: string;
  createdAt: string;
}

export interface MemberProfile {
  profileImageKey: string | null;
  birthDate: string | null;
  gender: string | null;
  heightCm: number | null;
  weightKg: number | null;
  activityLevel: string | null;
  healthGoal: string | null;
  diseases: string | null;
  allergyFoods: string | null;
  foodPreferences: string | null;
  eatsAlone: boolean | null;
  diningPartySize: "ONE" | "TWO" | "THREE_TO_FOUR" | "FIVE_PLUS" | null;
  profileCompletedAt: string | null;
}

export interface MemberDetail extends MemberSummary {
  id: number;
  authProvider: "EMAIL" | "KAKAO";
  subscribed: boolean;
  subscriptionStatus: "PAID" | "FREE_TRIAL" | "NONE";
  subscriptionCancelledAt: string | null;
  profile: MemberProfile;
  memos: MemberMemo[];
}

export interface RecommendationSummary {
  recommendationCode: string;
  requestedAt: string;
  memberName: string;
  ingredients: string;
  recipeCount: number;
  status: string;
}

export interface RecipeInfo {
  id: number;
  title: string;
  summary: string | null;
  cookingMinutes: number | null;
  difficulty: string | null;
  servings: number | null;
  nutritionJson: string | null;
  ingredientsJson: string | null;
  stepsJson: string | null;
  sortOrder: number;
}

export interface RecommendationDetail {
  recommendationCode: string;
  requestedAt: string;
  completedAt: string | null;
  memberCode: string;
  memberName: string;
  phone: string | null;
  ingredients: string;
  excludedIngredients: string | null;
  request: string | null;
  diningPartySize: "ONE" | "TWO" | "THREE_TO_FOUR" | "FIVE_PLUS";
  status: string;
  modelName: string | null;
  promptVersion: string | null;
  recommendationPoint: string | null;
  recipes: RecipeInfo[];
}

export interface SavedRecipeSummary {
  savedRecipeCode: string;
  savedAt: string;
  memberName: string;
  recipeTitle: string;
}

export interface SavedRecipeDetail {
  savedRecipeCode: string;
  savedAt: string;
  memberCode: string;
  memberName: string;
  phone: string | null;
  recommendationPoint: string | null;
  recipe: RecipeInfo;
}

export interface PaymentSummary {
  paymentCode: string;
  paidAt: string | null;
  status: string;
  memberName: string;
  phone: string | null;
  planName: string;
  subscriptionStartedAt: string | null;
  subscriptionEndedAt: string | null;
  amountKrw: number;
  method: string | null;
}

export interface PaymentDetail extends PaymentSummary {
  orderId: string;
  paymentKey: string | null;
  memberCode: string;
  memberStatus: string;
  durationMonths: number;
  planPriceKrw: number;
  approvedAt: string | null;
  cancelledAt: string | null;
  subscriptionCancelledAt: string | null;
  refundStatus: "NOT_REFUNDED" | "REFUNDED";
  refundEligible: boolean;
  refundEligibilityReason: string;
  aiRecipeProvidedCount: number;
  refundableAmountKrw: number;
  refundProcessedAt: string | null;
  failureCode: string | null;
  failureMessage: string | null;
  createdAt: string;
}

export interface InquirySummary {
  inquiryCode: string;
  title: string;
  category: string;
  memberName: string;
  createdAt: string;
  status: "WAITING" | "ANSWERED";
}

export interface InquiryDetail extends InquirySummary {
  memberCode: string;
  memberEmail: string;
  memberJoinedAt: string;
  content: string;
  answer: string | null;
  answeredAt: string | null;
}

function basicAuthorization(credentials: AdminCredentials) {
  const bytes = new TextEncoder().encode(`${credentials.id}:${credentials.password}`);
  let binary = "";
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return `Basic ${btoa(binary)}`;
}

export function requestAdminApi<T>(path: string, credentials: AdminCredentials, init?: RequestInit) {
  return requestApi<T>(`/admin${path}`, {
    ...init,
    headers: { Authorization: basicAuthorization(credentials), ...init?.headers }
  });
}

export function getMembers(credentials: AdminCredentials, query = "") {
  return requestAdminApi<PageData<MemberSummary>>(`/members${query}`, credentials);
}

export function getMember(credentials: AdminCredentials, memberCode: string) {
  return requestAdminApi<MemberDetail>(`/members/${encodeURIComponent(memberCode)}`, credentials);
}

export function updateMember(credentials: AdminCredentials, memberCode: string, body: Partial<Pick<MemberDetail, "name" | "phone" | "email" | "status">>) {
  return requestAdminApi<void>(`/members/${encodeURIComponent(memberCode)}`, credentials, { method: "PATCH", body: JSON.stringify(body) });
}

export function addMemberMemo(credentials: AdminCredentials, memberCode: string, content: string) {
  return requestAdminApi<MemberMemo>(`/members/${encodeURIComponent(memberCode)}/memos`, credentials, { method: "POST", body: JSON.stringify({ content }) });
}

export function deleteMemberMemo(credentials: AdminCredentials, memberCode: string, memoId: number) {
  return requestAdminApi<void>(`/members/${encodeURIComponent(memberCode)}/memos/${memoId}`, credentials, { method: "DELETE" });
}

export function cancelMemberSubscription(credentials: AdminCredentials, memberCode: string) {
  return requestAdminApi<void>(`/members/${encodeURIComponent(memberCode)}/subscription-cancel`, credentials, { method: "POST" });
}

export function getRecommendations(credentials: AdminCredentials, query = "") {
  return requestAdminApi<PageData<RecommendationSummary>>(`/recommendations${query}`, credentials);
}

export function getRecommendation(credentials: AdminCredentials, code: string) {
  return requestAdminApi<RecommendationDetail>(`/recommendations/${encodeURIComponent(code)}`, credentials);
}

export function getSavedRecipes(credentials: AdminCredentials, query = "") {
  return requestAdminApi<PageData<SavedRecipeSummary>>(`/saved-recipes${query}`, credentials);
}

export function getSavedRecipe(credentials: AdminCredentials, code: string) {
  return requestAdminApi<SavedRecipeDetail>(`/saved-recipes/${encodeURIComponent(code)}`, credentials);
}

export function getPayments(credentials: AdminCredentials, query = "") {
  return requestAdminApi<PageData<PaymentSummary>>(`/payments${query}`, credentials);
}

export function getPayment(credentials: AdminCredentials, paymentCode: string) {
  return requestAdminApi<PaymentDetail>(`/payments/${encodeURIComponent(paymentCode)}`, credentials);
}

export function cancelPaymentSubscription(credentials: AdminCredentials, paymentCode: string) {
  return requestAdminApi<void>(`/payments/${encodeURIComponent(paymentCode)}/subscription-cancel`, credentials, { method: "POST" });
}

export function refundPayment(credentials: AdminCredentials, paymentCode: string) {
  return requestAdminApi<{
    paymentCode: string;
    refundStatus: "REFUNDED";
    refundedAmountKrw: number;
    refundedAt: string;
  }>(`/payments/${encodeURIComponent(paymentCode)}/refund`, credentials, { method: "POST" });
}

export function getInquiries(credentials: AdminCredentials, query = "") {
  return requestAdminApi<PageData<InquirySummary>>(`/inquiries${query}`, credentials);
}

export function getInquiry(credentials: AdminCredentials, code: string) {
  return requestAdminApi<InquiryDetail>(`/inquiries/${encodeURIComponent(code)}`, credentials);
}

export function answerInquiry(credentials: AdminCredentials, code: string, answer: string) {
  return requestAdminApi<void>(`/inquiries/${encodeURIComponent(code)}/answer`, credentials, { method: "PUT", body: JSON.stringify({ answer }) });
}
